import type { ProfilePalette } from './profile-link-card';

/**
 * The OWNER-side data contract of Symphony Profile Links — what the owner screens (`/profile-links`) and the
 * visitor's `open-profile-thread` exchange with orion-web's API routes (docs/symphony-profile-links-design.md
 * §7.1, §8.2). Field names are the ones iOS and Android parse, so a field added here is added on all three
 * surfaces or on none (repo CLAUDE.md, three-surface parity: data contract).
 *
 * Mirrors the Phase 3 VMR `ProfileLink` entity, minus what a client must never hold: the owner's metaSiteId,
 * the referral-code id, the revision and the WhatsApp short code stay server-side. The PUBLIC face of a link
 * (what a visitor sees) is a different, smaller type: `PublicProfileCard` in `lib/profile-link-card.ts`.
 *
 * Pure types plus the closed enums, so the server route and the client share one spelling.
 */

export const PROFILE_LINK_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'ARCHIVED'] as const;
export type ProfileLinkStatus = (typeof PROFILE_LINK_STATUSES)[number];

/** What the owner's AI may do for a visitor without asking (§6.5). A server-side enum; unknown values are dropped. */
export const PROFILE_LINK_ACTIONS = ['MESSAGE', 'REQUEST_FILE', 'REQUEST_MEETING'] as const;
export type ProfileLinkAction = (typeof PROFILE_LINK_ACTIONS)[number];

/** Everything in here is PUBLIC verbatim — the page shows it and the visitor side reads it (§7.1). */
export interface ProfileLinkCard {
  headline: string;
  bio: string;
  /** "Ask me about…" chips. */
  topics: string[];
  /** The opening message, sent by code — never by a model (§6.6). */
  greeting: string;
  suggestedAsks: string[];
  design: { palette: ProfilePalette };
}

export interface ProfileLinkStats {
  views: number;
  appTaps: number;
  waTaps: number;
  /** People who made contact through this link. */
  contacts: number;
  signups: number;
  /** Credits the owner's AI spent answering on this link today (Q2: the owner pays). */
  creditsToday: number;
  /** The owner-set ceiling for `creditsToday`. 0 = no cap. Messages keep arriving past it (§11.2 B). */
  dailyCreditCap: number;
  /**
   * Views since midnight UTC, and over the last 7 UTC days (today included). Absent when the service did not send
   * them (a deploy from before the day counters, which started 2026-10-22): the screens then hide those two rows.
   */
  viewsToday?: number;
  viewsLast7Days?: number;
  /** Visitors who opened a chat through the link, whether or not they then wrote. Absent from an older service. */
  openedChats?: number;
}

export interface ProfileLink {
  id: string;
  /** The vanity handle — the `<handle>` of `/me/<handle>`. One per account. */
  handle: string;
  /** Absent on the main profile; the `<context>` of `/me/<handle>/<context>` otherwise. */
  contextSlug?: string;
  /** The context link's own label ("DevConf 26"). Absent on the main profile. */
  contextTitle?: string;
  status: ProfileLinkStatus;
  /** ISO-8601. Absent = never expires; the main profile never does (D7). */
  expiresAt?: string;
  card: ProfileLinkCard;
  /** PRIVATE — the owner's AI reads it; no visitor ever does (§7.1). */
  instructions: string;
  allowedActions: ProfileLinkAction[];
  stats: ProfileLinkStats;
}

/** Only what CHANGED (`patchFromDraft`): a write that restates unchanged values clobbers a concurrent edit by the Maestro. */
export interface ProfileLinkPatch {
  contextTitle?: string;
  card?: Partial<Omit<ProfileLinkCard, 'design'>> & { design?: { palette: ProfilePalette } };
  instructions?: string;
  allowedActions?: ProfileLinkAction[];
  /** `null` clears the expiry. */
  expiresAt?: string | null;
  dailyCreditCap?: number;
}

export interface ProfileLinksList {
  /** The account's claimed handle, or absent until one is claimed. */
  handle?: string;
  links: ProfileLink[];
  /**
   * Views across every link the account has — the main profile and its context links (VMR
   * `ListProfileLinksResponse.total_views`). What the Settings row shows; 0 when the server sent none.
   */
  totalViews?: number;
}

export type ProfileLeadChannel = 'WHATSAPP' | 'APP';
/** `AI_ANSWERED` = handled inside `allowedActions`; `WAITING_FOR_YOU` = raised to the owner (§6.5, §8.3). */
export type ProfileLeadState = 'AI_ANSWERED' | 'WAITING_FOR_YOU';

/**
 * What the People row says about a lead, read from its owner-side conversation by the native-data bridge
 * (`profileLeadActivity.ts`): an open decision for the owner (`needs_you`), the AI wrote back (`ai_answered`), or
 * nobody has yet (`new`). Absent from an older bridge — `leadStatus` then falls back to `state`.
 */
export type ProfileLeadStatus = 'needs_you' | 'ai_answered' | 'new';
export const PROFILE_LEAD_STATUSES: readonly ProfileLeadStatus[] = ['needs_you', 'ai_answered', 'new'];

/** One person who wrote in. Name is self-asserted; the phone is always shown (Q5). */
export interface ProfileLead {
  id: string;
  name: string;
  channel: ProfileLeadChannel;
  phone?: string;
  /** The visitor's latest message (from the owner-side conversation); '' when none was found. */
  summary: string;
  /** ISO-8601. */
  at: string;
  state: ProfileLeadState;
  status?: ProfileLeadStatus;
  /** The owner-side thread — where the row opens. */
  conversationId?: string;
  /**
   * The owner → visitor outreach the thread lives on. Once the visitor writes, the contact book lists this same
   * outreach under the person it resolved to — so People matches a lead to a contact it already shows by it
   * (`matchLeadsToContacts`). Absent from an older server, or before the outreach exists.
   */
  outreachId?: string;
}

export interface ProfileLeads {
  leads: ProfileLead[];
  /** Today's digest numbers (§8.3); absent when nobody wrote today. */
  digest?: { wroteToday: number; wantMeeting: number };
}

export type HandleRefusal = 'taken' | 'invalid' | 'reserved';
export const HANDLE_REFUSALS: readonly HandleRefusal[] = ['taken', 'invalid', 'reserved'];

export type HandleCheck = { available: true } | { available: false; reason: HandleRefusal };

/** What `claim-handle` answers: the claimed handle and its drafted main profile, or why nothing was claimed. */
export type ClaimHandleResult = { handle: string; link: ProfileLink } | { refusal: HandleRefusal };

/**
 * What `prefill-card` did: Symphony drafted the EMPTY card fields from what it knows about the owner (their
 * business knowledge, the professional facts it holds, a connected LinkedIn). The DRAFT only — nothing goes
 * public until the owner publishes. `filledFields` are wire names (`headline`, `suggested_asks`, …).
 */
export interface ProfileCardPrefill {
  /**
   * `insufficient_info`: Symphony holds something, but nothing it could tie to a headline or a bio, so it wrote
   * nothing rather than generic filler. `skipped`: an automatic run inside its daily throttle.
   */
  outcome: 'filled' | 'nothing_empty' | 'nothing_known' | 'insufficient_info' | 'skipped' | 'unavailable';
  filledFields: string[];
  sources: string[];
}

export interface PrefillCardResult {
  link: ProfileLink;
  prefill: ProfileCardPrefill;
}

/**
 * What `open-profile-thread` answers (§6.1). Never the owner's metasite or recipient id — a conversation the
 * CALLER can open, plus the public name and context for the thread header. `own_link` is the owner opening
 * their own link: they get the editor, never a thread with themselves (§6.1 step 5).
 *
 * The wire also answers `{ kind: 'refused', reason: 'link_inactive' | 'busy' | 'blocked' }`; the client
 * (`lib/profile-links-api.ts`) turns it into a `ProfileLinksApiError` — `link_inactive` as the 404 every miss
 * is, the other two as a failure to open — so a screen only ever renders a thread, the editor, or an error.
 */
export type OpenProfileThreadResult =
  | { kind: 'thread'; conversationId: string; ownerName: string; contextTitle?: string }
  | { kind: 'own_link'; linkId: string };

/**
 * Field ceilings, enforced by the API route on the way in and by the editor as the owner types. The VMR
 * service is the authority (Phase 3); these only keep an over-long write from reaching it.
 */
export const PROFILE_LINK_LIMITS = {
  contextTitle: 60,
  headline: 120,
  bio: 400,
  greeting: 400,
  topic: 60,
  topics: 6,
  suggestedAsk: 80,
  suggestedAsks: 4,
  instructions: 2000,
  /** Credits per day. A cap above this is a typo, not a budget. */
  dailyCreditCap: 100000,
} as const;
